'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackHdpSimilarListingClickEvent,
  emitTrackImpressionForPropertyCardEvent,
} from '@/src/analytics/hotpads/events';
import { toHotpadsClickstreamListing } from '@/src/analytics/hotpads/track';
import { useRouter } from '@/src/i18n/navigation';
import { useTranslations } from 'next-intl';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { LucideIcon } from '@/src/components/LucideIcon';
import { FederatedPhoto } from '@/src/components/FederatedPhoto';
import { IconButton, ShowHide, Table, VisuallyHidden } from '@zillow/constellation-10';
import { TotalMonthlyPriceIndicator } from '@/src/components/features/listings/TotalMonthlyPrice';
import type { TotalMonthlyPriceListing } from '@/src/lib/utils/totalMonthlyPrice';
import { analytics } from '@/src/components/features/property/lib';
import type { CellValue, ComparisonRow } from './comparisonTableUtils';
import { formatCellValue, STATIC_CATEGORIES } from './comparisonTableUtils';
import { useSimilarListingsFetch } from './useSimilarListingsFetch';
import { useScrollAffordance } from './useScrollAffordance';
import * as styles from './similarListingsModule.styles';

const renderComparableValue = (value: CellValue) => {
  if (value === true) {
    return <LucideIcon icon={Check} size="sm" className={styles.checkmarkIcon} title="Yes" />;
  }

  if (value === false) {
    return (
      <>
        <VisuallyHidden>No</VisuallyHidden>
        <span aria-hidden="true">{formatCellValue(null)}</span>
      </>
    );
  }

  return formatCellValue(value);
};

type ListingHeaderColumnProps = {
  row: ComparisonRow;
  isCurrent: boolean;
  onClick: (e: React.MouseEvent<HTMLAnchorElement>, listing: ComparisonRow['listing']) => void;
  onImpression: (listing: ComparisonRow['listing']) => void;
};

const ListingHeaderColumn = memo(function ListingHeaderColumn({
  row,
  isCurrent,
  onClick,
  onImpression,
}: ListingHeaderColumnProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => onClick(e, row.listing),
    [onClick, row.listing],
  );
  const linkRef = useRef<HTMLAnchorElement>(null);
  const impressionSent = useRef(false);
  useEffect(() => {
    const link = linkRef.current;
    if (!link || impressionSent.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (impressionSent.current || !entries.some((entry) => entry.isIntersecting)) return;
      impressionSent.current = true;
      onImpression(row.listing);
      observer.disconnect();
    });
    observer.observe(link);
    return () => observer.disconnect();
  }, [onImpression, row.listing]);

  const headerContent = (
    <>
      {row.image ? (
        <div className={styles.listingImageWrapper}>
          <FederatedPhoto src={row.image} alt="" defaultSize={384} className={styles.listingImage} loading="lazy" />
        </div>
      ) : (
        <div className={styles.noPhotoPlaceholder}>No photo</div>
      )}
      {isCurrent ? (
        <div className={styles.currentListingText}>
          <p className={styles.currentListingLabel}>This listing</p>
          <h3 className={styles.currentListingName}>{row.title}</h3>
        </div>
      ) : (
        <h3 className={styles.listingName}>{row.title}</h3>
      )}
    </>
  );

  return (
    <Table.HeaderCell
      scope="col"
      className={isCurrent ? styles.currentListingHeader : styles.listingHeader}
      aria-label={isCurrent ? `This listing, ${row.title}` : row.title}
    >
      {isCurrent || !row.link ? (
        <div className={`${styles.columnHeader} ${styles.columnHeaderCurrent}`}>{headerContent}</div>
      ) : (
        <a href={row.link} onClick={handleClick} onAuxClick={handleClick} ref={linkRef} className={styles.columnHeader}>
          {headerContent}
        </a>
      )}
    </Table.HeaderCell>
  );
});

type Props = {
  listing: PropertyDetailListing;
};

/**
 * SimilarListingsModule — comparison table on the HDP.
 *
 * Each column is a listing (current first, then similar listings). Rows
 * compare prices per bedroom and amenities side by side. Sticky labels
 * column, total-price badge per column, zoom-on-hover thumbnails, smarter
 * left/right scroll affordance.
 *
 * Data source is `useSimilarListingsFetch`, which reads embedded v3 detail
 * similar listings and falls back to v3 detail fetches when needed.
 */
export const SimilarListingsModule = ({ listing }: Props) => {
  const router = useRouter();
  const t = useTranslations('hdp');
  const [isHidden, setIsHidden] = useState(true);

  const { rows, bedroomLabels, hasCurrentListingColumn } = useSimilarListingsFetch(listing);
  const { scrollRef, canScrollLeft, canScrollRight, handleScroll } = useScrollAffordance({
    rowsLength: rows.length,
  });

  const breadcrumbs = (
    listing as unknown as {
      areas?: { breadcrumbs?: Array<{ name?: string; resourceId?: string }> };
    } | null
  )?.areas?.breadcrumbs;

  const trackImpression = useCallback((row: ComparisonRow['listing']) => {
    emitTrackImpressionForPropertyCardEvent({
      listing: toHotpadsClickstreamListing(row),
      triggerLocation: 'similar_listings_module',
    });
    const listingId =
      (row as { maloneLotIdEncoded?: string }).maloneLotIdEncoded ??
      (row as { lotIdEncoded?: string }).lotIdEncoded ??
      (row as { aliasEncoded?: string }).aliasEncoded;
    analytics.sendEventToApi('similar_listing_impression', {
      maloneLotIdEncoded: listingId,
    });
  }, []);

  const handleListingClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, item: ComparisonRow['listing']) => {
      if (e.defaultPrevented || (e.button !== 0 && e.button !== 1)) return;
      emitTrackHdpSimilarListingClickEvent({ listing, triggerLocation: 'home_details' });
      emitTrackClicksIntoPropertyEvent({
        listing: toHotpadsClickstreamListing(item),
        triggerLocation: 'similar_listings_module',
      });
      analytics.sendEventToApi('similar_listing_click', {
        triggerLocation: 'home_details_halo',
      });
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      const href =
        (item as { uriV2?: string; uriMalone?: string; uriBuilding?: string }).uriV2 ??
        (item as { uriV2?: string; uriMalone?: string; uriBuilding?: string }).uriMalone ??
        (item as { uriV2?: string; uriMalone?: string; uriBuilding?: string }).uriBuilding;
      if (href) router.push(href as Parameters<typeof router.push>[0]);
    },
    [listing, router],
  );

  const handleScrollLeft = useCallback(() => handleScroll('left'), [handleScroll]);
  const handleScrollRight = useCallback(() => handleScroll('right'), [handleScroll]);

  const mostSpecificArea = useMemo(() => breadcrumbs?.[breadcrumbs.length - 1], [breadcrumbs]);

  const areaUri = mostSpecificArea?.resourceId ? `/${mostSpecificArea.resourceId}` : '/';

  const handleAreaLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      router.push(areaUri as Parameters<typeof router.push>[0]);
    },
    [router, areaUri],
  );

  if (rows.length === 0) {
    return null;
  }

  const showAnyTotalBadge = rows.some(
    (row) => (row.listing as { hasTotalMonthlyPrice?: boolean }).hasTotalMonthlyPrice === true,
  );
  const buttonLabel = isHidden ? t('showMore') : t('showLess');
  const buttonAriaLabel = `${buttonLabel}: ${t('similarListingsTitle')}`;

  return (
    <ShowHide.Root
      id="similar-listings-module-details"
      appearance="block"
      isHidden={isHidden}
      onDisplayChange={() => setIsHidden((prev) => !prev)}
      renderButton={() => (
        <ShowHide.Button aria-controls="similar-listings-module-details" aria-label={buttonAriaLabel}>
          {buttonLabel}
        </ShowHide.Button>
      )}
    >
      <div className={styles.container}>
        <div className={styles.tableWrapper}>
          {(canScrollLeft || canScrollRight) && (
            <div className={styles.arrowRail} data-testid="similar-listings-arrow-rail">
              <div className={styles.arrowSlot}>
                {canScrollLeft && (
                  <IconButton
                    data-testid="button-scroll-similar-listings-prev"
                    shape="circle"
                    tone="neutral-fixed"
                    emphasis="filled"
                    onImpact
                    size="sm"
                    title="Scroll to previous similar listing"
                    className={styles.arrowButton}
                    onClick={handleScrollLeft}
                  >
                    <LucideIcon icon={ChevronLeft} size="md" />
                  </IconButton>
                )}
              </div>
              <div className={styles.arrowSlot}>
                {canScrollRight && (
                  <IconButton
                    data-testid="button-scroll-similar-listings-next"
                    shape="circle"
                    tone="neutral-fixed"
                    emphasis="filled"
                    onImpact
                    size="sm"
                    title="Scroll to next similar listing"
                    className={styles.arrowButton}
                    onClick={handleScrollRight}
                  >
                    <LucideIcon icon={ChevronRight} size="md" />
                  </IconButton>
                )}
              </div>
            </div>
          )}

          <Table.Responsive ref={scrollRef as React.Ref<HTMLDivElement>} className={styles.scrollContainer}>
            <Table.Root
              aria-label={t('similarListingsComparison')}
              appearance="zebra"
              size="sm"
              className={styles.table}
            >
              <colgroup>
                <col className={styles.rowLabelColumn} />
                {rows.map((row, columnIndex) => (
                  <col
                    key={`col-${(row.listing as { maloneLotIdEncoded?: string }).maloneLotIdEncoded ?? columnIndex}`}
                    className={styles.listingColumn}
                  />
                ))}
              </colgroup>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell scope="col" align="right" className={styles.rowLabelHeader}>
                    <VisuallyHidden>{t('similarListings')}</VisuallyHidden>
                  </Table.HeaderCell>
                  {rows.map((row, columnIndex) => (
                    <ListingHeaderColumn
                      key={`${(row.listing as { maloneLotIdEncoded?: string }).maloneLotIdEncoded ?? columnIndex}`}
                      row={row}
                      isCurrent={hasCurrentListingColumn && columnIndex === 0}
                      onClick={handleListingClick}
                      onImpression={trackImpression}
                    />
                  ))}
                </Table.Row>
              </Table.Header>
              {!isHidden && (
                <Table.Body>
                  {bedroomLabels.map((label, rowIndex) => (
                    <Table.Row key={`price-row-${label}`}>
                      <Table.HeaderCell
                        scope="row"
                        align="right"
                        className={`${styles.rowLabelCell} ${rowIndex === 0 ? styles.firstBodyRow : ''}`}
                      >
                        {label}
                      </Table.HeaderCell>
                      {rows.map((row, columnIndex) => (
                        <Table.Cell
                          key={`price-${label}-${columnIndex}`}
                          className={`${styles.valueCell} ${styles.priceCell} ${
                            rowIndex === 0 ? styles.firstBodyRow : ''
                          }`}
                        >
                          {formatCellValue(row.prices[label] ?? null)}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}

                  {showAnyTotalBadge && (
                    <Table.Row>
                      <Table.HeaderCell scope="row" align="right" className={styles.rowLabelCell}>
                        <VisuallyHidden>Total monthly price</VisuallyHidden>
                      </Table.HeaderCell>
                      {rows.map((row, columnIndex) => (
                        <Table.Cell
                          key={`total-price-${columnIndex}`}
                          className={`${styles.valueCell} ${styles.totalPriceBadgeCell}`}
                        >
                          <TotalMonthlyPriceIndicator
                            listing={row.listing as TotalMonthlyPriceListing}
                            feesMode="always"
                          />
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  )}

                  {STATIC_CATEGORIES.map((category) => {
                    return (
                      <Table.Row key={`amenity-row-${category}`}>
                        <Table.HeaderCell scope="row" align="right" className={styles.rowLabelCell}>
                          {category}
                        </Table.HeaderCell>
                        {rows.map((row, columnIndex) => (
                          <Table.Cell key={`amenity-${category}-${columnIndex}`} className={styles.valueCell}>
                            {renderComparableValue(row.amenities[category])}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    );
                  })}
                </Table.Body>
              )}
            </Table.Root>
          </Table.Responsive>
        </div>

        <ShowHide.Hidden>
          {mostSpecificArea?.name && (
            <div className={styles.areaLinkContainer}>
              <a href={areaUri} onClick={handleAreaLinkClick} className={styles.areaLink}>
                {t('seeMoreListingsIn', { area: mostSpecificArea.name })}
                <span aria-hidden="true">&rsaquo;</span>
              </a>
            </div>
          )}
        </ShowHide.Hidden>
      </div>
    </ShowHide.Root>
  );
};
